import type {
  CreatePollDTO,
  IPollRepository,
  PaginatedResponse,
  QueryParams,
  UpdatePollDTO,
  VoteDTO,
} from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type { DetailedVote, Poll, PollResults } from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

// Tipos de respuesta de la API
interface ApiPollOptionResponse {
  id: string;
  text: string;
  displayOrder?: number;
  votes?: number;
  percentage?: number;
}

interface ApiDetailedVote {
  optionId: string;
  optionText: string;
  userId: string;
  displayName: string;
  unitId?: string;
  unitName?: string;
  votedAt: string;
}

interface ApiPollResponse {
  id: string;
  title: string;
  description?: string | null;
  status: 'DRAFT' | 'ACTIVE' | 'CLOSED';
  allowMultiple: boolean;
  isAnonymous: boolean;
  startsAt: string;
  endsAt: string;
  createdBy: string;
  sectionIds?: string[];
  options: ApiPollOptionResponse[] | string[];
  createdAt: string;
  hasVoted?: boolean;
  userVotedOptionIds?: string[];
  totalVotes?: number;
  voterCount?: number;
  unitsVoted?: number;
  totalUnits?: number;
  detailedVotes?: ApiDetailedVote[];
}

interface ApiPollResultsResponse {
  pollId: string;
  title: string;
  totalVotes: number;
  voterCount: number;
  results: Array<{ optionId?: string; text?: string; option?: string; votes: number; percentage: number }> | string[];
  userVoted: boolean;
  userVotes?: string[];
  detailedVotes?: ApiDetailedVote[];
}

const mapApiDetailedVotes = (votes?: ApiDetailedVote[]): DetailedVote[] | undefined => {
  if (!votes || votes.length === 0) return undefined;
  return votes.map((v) => ({
    optionId: v.optionId,
    optionText: v.optionText,
    userId: v.userId,
    displayName: v.displayName,
    unitId: v.unitId,
    unitName: v.unitName,
    votedAt: v.votedAt,
  }));
};

// Mapea la respuesta de la API al tipo Poll interno
const mapApiPollToPoll = (apiPoll: ApiPollResponse): Poll => {
  // Manejar opciones como string[] o como array de objetos
  const mappedOptions = apiPoll.options.map((opt, index) => {
    if (typeof opt === 'string') {
      return {
        id: `${apiPoll.id}-opt-${index}`,
        text: opt,
        votes: 0,
      };
    }
    return {
      id: opt.id || `${apiPoll.id}-opt-${index}`,
      text: opt.text,
      votes: opt.votes || 0,
      displayOrder: opt.displayOrder,
      percentage: opt.percentage,
    };
  });

  return {
    id: apiPoll.id,
    title: apiPoll.title,
    description: apiPoll.description || undefined,
    status: apiPoll.status,
    allowMultiple: apiPoll.allowMultiple,
    isAnonymous: apiPoll.isAnonymous,
    startsAt: apiPoll.startsAt,
    endsAt: apiPoll.endsAt,
    createdBy: apiPoll.createdBy,
    sectionIds: apiPoll.sectionIds ?? [],
    options: mappedOptions,
    createdAt: apiPoll.createdAt,
    hasVoted: apiPoll.hasVoted ?? false,
    userVotedOptionIds: apiPoll.userVotedOptionIds || [],
    totalVotes: apiPoll.totalVotes,
    voterCount: apiPoll.voterCount,
    unitsVoted: apiPoll.unitsVoted,
    totalUnits: apiPoll.totalUnits,
    detailedVotes: mapApiDetailedVotes(apiPoll.detailedVotes),
  };
};

// Mapea la respuesta de la API al tipo PollResults interno
const mapApiPollResultsToPollResults = (apiResults: ApiPollResultsResponse): PollResults => {
  let results: Array<{ optionId?: string; text?: string; option?: string; votes: number; percentage: number }>;
  if (Array.isArray(apiResults.results) && apiResults.results.length > 0) {
    if (typeof apiResults.results[0] === 'string') {
      results = (apiResults.results as string[]).map((option) => ({
        option,
        votes: 0,
        percentage: 0,
      }));
    } else {
      // Normalizar: el backend puede enviar { optionId, text } o { option }
      results = (apiResults.results as Array<{ optionId?: string; text?: string; option?: string; votes: number; percentage: number }>).map((r) => ({
        optionId: r.optionId,
        text: r.text,
        option: r.option || r.text,
        votes: r.votes,
        percentage: r.percentage,
      }));
    }
  } else {
    results = [];
  }

  return {
    pollId: apiResults.pollId,
    title: apiResults.title,
    totalVotes: apiResults.totalVotes,
    voterCount: apiResults.voterCount,
    results,
    userVoted: apiResults.userVoted,
    userVotes: apiResults.userVotes,
    detailedVotes: mapApiDetailedVotes(apiResults.detailedVotes),
  };
};

export class ApiPollRepository implements IPollRepository {
  async getActivePolls(): Promise<Poll[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiPollResponse>>('v1/polls/active');
    return response.data.map(mapApiPollToPoll);
  }

  async getClosedPolls(): Promise<Poll[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiPollResponse>>('v1/polls/closed');
    return response.data.map(mapApiPollToPoll);
  }

  async getPollById(id: string): Promise<Poll | null> {
    const client = getApiClient();
    try {
      const poll = await client.get<ApiPollResponse>(`v1/polls/${id}`);
      return mapApiPollToPoll(poll);
    } catch {
      return null;
    }
  }

  async getPollResults(id: string): Promise<PollResults> {
    const client = getApiClient();
    const results = await client.get<ApiPollResultsResponse>(`v1/polls/${id}/results`);
    return mapApiPollResultsToPollResults(results);
  }

  async vote(pollId: string, vote: VoteDTO): Promise<void> {
    const client = getApiClient();
    await client.post(`v1/polls/${pollId}/vote`, vote);
  }

  async getAllPolls(): Promise<Poll[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiPollResponse>>('v1/polls');
    return response.data.map(mapApiPollToPoll);
  }

  async getPolls(params?: QueryParams): Promise<PaginatedResponse<Poll>> {
    const client = getApiClient();
    // El backend acepta `status`; sin propagarlo el filtro por estado se
    // aplicaba en el cliente sobre la primera página.
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      status: params?.status,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      ...params,
    });
    const response = await client.get<PaginatedResponse<ApiPollResponse>>(
      `v1/polls?${queryString}`
    );
    return { ...response, data: response.data.map(mapApiPollToPoll) };
  }

  async createPoll(poll: CreatePollDTO): Promise<Poll> {
    const client = getApiClient();
    const apiPoll = await client.post<ApiPollResponse>('v1/polls', poll);
    return mapApiPollToPoll(apiPoll);
  }

  async updatePoll(id: string, poll: UpdatePollDTO): Promise<Poll> {
    const client = getApiClient();
    const apiPoll = await client.put<ApiPollResponse>(`v1/polls/${id}`, poll);
    return mapApiPollToPoll(apiPoll);
  }

  async publishPoll(id: string): Promise<Poll> {
    const client = getApiClient();
    const apiPoll = await client.patch<ApiPollResponse>(`v1/polls/${id}/publish`);
    return mapApiPollToPoll(apiPoll);
  }

  async closePoll(id: string): Promise<Poll> {
    const client = getApiClient();
    const apiPoll = await client.patch<ApiPollResponse>(`v1/polls/${id}/close`);
    return mapApiPollToPoll(apiPoll);
  }

  async deletePoll(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/polls/${id}`);
  }
}

